import React, { memo, Suspense, useCallback, useMemo, useState } from 'react';

import { useTranslation } from '@lyearn/i18n';
import { cn, css } from '@lyearn/style';
import { Stack } from '@lyearn/ui';
import { FieldOverrides } from '@/components/Form';
import Loader from '@/components/Loader';
import { Dialog } from '@/components/Modals';
import MultiStepForm from '@/components/MultiStepForm';
import { getRandomCardImage } from '@/utils/cardImage';

import { CreateClientContactFormValueType, CreateClientContactModalProps } from './types';
import {
  getCreateClientContactFormInitialValues,
  getCreateClientFormFields,
  getSectionLayout,
  validateCreateClientContactForm,
} from './utils';

const OVERRIDES: FieldOverrides = {
  Section: {
    spacing: 'space-y-40',
    Name: {
      variant: 'heading-s',
    },
    Description: {
      variant: 'body-short-02',
      color: 'text-text-secondary',
    },
    Header: {
      className: 'mb-40 ms-8 space-y-4',
    },
  },
  spacing: {
    row: 'space-y-24',
  },
};

const dialogSize = css`
  max-width: 91.7rem;
  height: 68rem;
`;

const CreateClientContactModal: React.FC<CreateClientContactModalProps> = (props) => {
  const { onClose, onSubmit: onSubmitProp, initialFormValues, isEditing } = props;
  const { t } = useTranslation('Clients');

  const [isSubmitting, setIsSubmitting] = useState(false);
  const defaultCover = useMemo(() => getRandomCardImage('EVENT'), []);

  const [formikSubmit, setFormikSubmit] = useState<() => void>(() => {});
  const initialValues = useMemo(
    () => initialFormValues ?? getCreateClientContactFormInitialValues(),
    [initialFormValues],
  );
  const formFields = useMemo(
    () => getCreateClientFormFields({ t, defaultCover, isEditing }),
    [defaultCover, isEditing, t],
  );
  const validate = useMemo(() => validateCreateClientContactForm(t), [t]);
  const sections = useMemo(() => getSectionLayout({ t }), [t]);

  const onSubmit = useCallback(
    async (data: CreateClientContactFormValueType) => {
      setIsSubmitting(true);
      await onSubmitProp(data)
        .then(() => {
          onClose();
        })
        .finally(() => {
          setIsSubmitting(false);
        });
    },
    [onClose, onSubmitProp],
  );

  return (
    <Dialog
      open
      action={{
        disabled: isSubmitting,
        isSubmitting,
        text: isEditing
          ? t('Common:Actions.SaveChanges')
          : t('Clients:ContactEditorModal.SaveContact'),
        onSubmit: formikSubmit,
        buttonSize: 'large',
      }}
      className="h-full"
      classes={{ paper: cn('px-0 w-full rounded-16', dialogSize) }}
      closeButton={{
        size: 'large',
        className: 'end-12 top-20',
      }}
      header={{
        title: isEditing
          ? t('Clients:ContactEditorModal.EditTitle')
          : t('Clients:ContactEditorModal.CreateTitle'),
        description: isEditing
          ? t('Clients:ContactEditorModal.EditDescription')
          : t('Clients:ContactEditorModal.CreateDescription'),
        variants: {
          description: 'body-short-01',
        },
        className: 'pt-28 pb-24',
      }}
      onClose={onClose}>
      <Stack className="h-full flex-1 border-t border-b border-divider">
        <Suspense fallback={<Loader className="mx-auto my-auto" />}>
          <MultiStepForm<CreateClientContactFormValueType>
            hideBackNavigation
            hideNextNavigation
            fields={formFields}
            formName="CreateContact"
            initialValues={initialValues}
            overrides={OVERRIDES}
            sections={sections}
            setSubmitRef={setFormikSubmit}
            stepStateHandler="state"
            validate={validate}
            onSubmit={onSubmit}
          />
        </Suspense>
      </Stack>
    </Dialog>
  );
};

export default memo(CreateClientContactModal);
